import { useState } from 'react'
import { AlertCircle, CloudOff, Loader2, RefreshCw } from 'lucide-react'
import { useRxDB } from '@/lib/db/rxdb/hooks'
import useSyncStore from '@/store/SyncStore'
import { useSidebar } from '@/components/ui/sidebar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { markApiDown, markApiHealthy, probeApiReachable } from '@/lib/api/apiHealth'

type ConnectionStatus = 'healthy' | 'api_down' | 'sync_down' | 'syncing' | 'error'

type StatusConfig = {
  icon: typeof CloudOff
  label: string
  description: string
  tone: 'ok' | 'warn' | 'error' | 'info'
  animate?: boolean
}

const toneStyles: Record<StatusConfig['tone'], { dot: string; text: string }> = {
  ok: { dot: 'bg-green-500', text: 'text-muted-foreground' },
  warn: { dot: 'bg-yellow-500', text: 'text-yellow-600 dark:text-yellow-500' },
  error: { dot: 'bg-destructive', text: 'text-destructive' },
  info: { dot: 'bg-blue-500', text: 'text-blue-500' },
}

export function ConnectionStatusIndicator() {
  const { state: sidebarState } = useSidebar()
  const isCollapsed = sidebarState === 'collapsed'
  const { isSyncing, error, categorizedError, reset } = useRxDB()
  const apiStatus = useSyncStore((s) => s.apiStatus)
  const apiLastCheckedAt = useSyncStore((s) => s.apiLastCheckedAt)
  const [isChecking, setIsChecking] = useState(false)

  const status: ConnectionStatus = (() => {
    if (apiStatus === 'down') return 'api_down'
    if (isSyncing) return 'syncing'
    if (error) {
      return categorizedError?.category === 'network' ? 'sync_down' : 'error'
    }
    return 'healthy'
  })()

  // Healthy — hide indicator entirely
  if (status === 'healthy') return null

  const config: StatusConfig = (() => {
    switch (status) {
      case 'api_down':
        return {
          icon: CloudOff,
          label: 'Offline',
          description: 'Server unreachable. Local changes will sync when reconnected.',
          tone: 'warn',
        }
      case 'sync_down':
        return {
          icon: CloudOff,
          label: 'Sync paused',
          description: 'Sync is offline. Your changes are saved locally.',
          tone: 'warn',
        }
      case 'syncing':
        return {
          icon: Loader2,
          label: 'Syncing...',
          description: 'Synchronizing your data.',
          tone: 'info',
          animate: true,
        }
      case 'error':
        return {
          icon: AlertCircle,
          label: 'Sync error',
          description: categorizedError?.message || 'Unable to sync data.',
          tone: 'error',
        }
    }
  })()

  const handleRetry = async () => {
    setIsChecking(true)
    try {
      const reachable = await probeApiReachable()
      if (reachable) {
        markApiHealthy()
      } else {
        markApiDown()
      }
      if (status === 'error' || status === 'sync_down') {
        reset()
      }
    } finally {
      setIsChecking(false)
    }
  }

  const Icon = config.icon
  const tone = toneStyles[config.tone]

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-accent',
            isCollapsed && 'justify-center px-0',
          )}
        >
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span
              className={cn(
                'absolute inline-flex h-full w-full rounded-full opacity-75',
                tone.dot,
                config.tone !== 'ok' && 'animate-ping',
              )}
            />
            <span className={cn('relative inline-flex h-2 w-2 rounded-full', tone.dot)} />
          </span>
          {!isCollapsed && (
            <span className={cn('truncate font-medium', tone.text)}>
              {config.label}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-64">
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <Icon
              className={cn(
                'mt-0.5 h-4 w-4 flex-shrink-0',
                tone.text,
                config.animate && 'animate-spin',
              )}
            />
            <div className="space-y-1">
              <p className={cn('text-sm font-medium', tone.text)}>{config.label}</p>
              <p className="text-xs text-muted-foreground">{config.description}</p>
            </div>
          </div>
          {apiLastCheckedAt && (
            <p className="text-[10px] text-muted-foreground">
              Last checked {formatRelativeTime(new Date(apiLastCheckedAt))}
            </p>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleRetry}
            disabled={isChecking}
            className="w-full"
          >
            {isChecking ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Retry now
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function formatRelativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60)
    return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`
  }
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600)
    return `${h} ${h === 1 ? 'hour' : 'hours'} ago`
  }
  const d = Math.floor(seconds / 86400)
  return `${d} ${d === 1 ? 'day' : 'days'} ago`
}
